import { useDrop } from 'react-dnd';
import { DownOutlined } from '@ant-design/icons';
import { Empty, Button, Dropdown } from 'design-system';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import './templateCanvas.css';
import { Subject, ItemTypes } from './DraggableSubjectItem';
import type { TableData, ColumnSetting, ExtendedSubject } from './TableTemplates';
import { getTemplateData, getTemplateByType, setCurrentColumnSettings } from './TableTemplates';

// 定义拖拽项的类型接口，扩展Subject以包含拖拽过程中添加的属性
interface DragItem extends Subject {
  _dragTime?: string;
  _dragName?: string;
  _dragFullName?: string;
  _originalIndex?: number;
  _originalDragName?: string;
  _originalDragId?: string;
}

interface TemplateCanvasProps {
  templateType: string;
  canvasItems: ExtendedSubject[];
  onAddItem?: (item: ExtendedSubject) => void;
  onRemoveItem?: (itemId: string) => void;
  selectedRowId?: string | null;
  rowBackgroundColors?: Map<string, string>;
  onRowSelect?: (rowId: string) => void;
  onTableDataChange?: (tableData: TableData) => void;
  columnSettings?: ColumnSetting[];
  reportType?: string;
  readonly?: boolean;
  frequencyType?: string;
  allSubjects?: ExtendedSubject[];
}
const TemplateCanvas: React.FC<TemplateCanvasProps> = ({ 
  templateType, 
  canvasItems, 
  onAddItem,
  onRemoveItem,
  selectedRowId,
  rowBackgroundColors,
  onRowSelect,
  onTableDataChange,
  columnSettings,
  reportType,
  readonly,
  frequencyType,
  allSubjects
}) => {
  const dropRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [zoomLevel, setZoomLevel] = useState<string>('100%');
  const [customTransform, setCustomTransform] = useState<string | null>(null);
  const [specialZoomType, setSpecialZoomType] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
  const templateInstanceId = useRef<string>(`${templateType || 'default'}_${Date.now()}`);
  const prevZoomLevelRef = useRef<string>('100%');
  
  // 添加canvas项目数量的引用，用于调试比较
  const prevCanvasItemsCountRef = useRef<number>(canvasItems.length);
  
  
  // 监控props变化
  useEffect(() => {
    if (canvasItems.length !== prevCanvasItemsCountRef.current) {
      prevCanvasItemsCountRef.current = canvasItems.length;
    }
  }, [canvasItems]);
  
  useEffect(() => {
    templateInstanceId.current = `${templateType || 'default'}_${Date.now()}`;
  }, [templateType]);
  const onTableDataChangeRef = useRef(onTableDataChange);
  useEffect(() => {
    onTableDataChangeRef.current = onTableDataChange;
  }, [onTableDataChange]);
  useEffect(() => {
    if (columnSettings) {
      setCurrentColumnSettings(columnSettings);
    }
  }, [columnSettings]);
  // 添加错误边界和清理逻辑
  useEffect(() => () => {
      // 组件卸载时清理拖拽状态
      setIsDraggingOver(false);
    }, []);

  // 添加错误处理
  const handleDropError = useCallback((error: Error) => {
    setIsDraggingOver(false);
  }, []);

  const handleCanvasDrop = useCallback((ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    setIsDraggingOver(false);

    try {
      // 提取拖放数据
      const jsonData = ev.dataTransfer.getData('application/json');
      if (!jsonData) {
        return;
      }

      // 解析拖放的科目数据
      const draggedItem = JSON.parse(jsonData);
      
      // 确保拖放项有ID和名称
      if (!draggedItem.id || !draggedItem.name) {
        return;
      }
      
      // 准备添加到画布的科目数据
      const subjectToAdd: ExtendedSubject = {
        id: draggedItem.id,
        name: draggedItem.name,
        parentId: draggedItem.parentId,
        parentName: draggedItem.parentName,
        grandParentName: draggedItem.grandParentName,
        assessmentDimension: draggedItem.assessmentDimension
          ? {
              id: draggedItem.assessmentDimension.id,
              name: draggedItem.assessmentDimension.name,
              createdAt: draggedItem.assessmentDimension.createdAt || '',
              updatedAt: draggedItem.assessmentDimension.updatedAt || '',
              enabled: typeof draggedItem.assessmentDimension.enabled === 'boolean' ? draggedItem.assessmentDimension.enabled : true,
            }
          : undefined,
        _dragName: draggedItem.name,
        _dragFullName: draggedItem.name,
        _originalDragName: draggedItem.name,
        _originalDragId: draggedItem.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      
      // 添加到画布
      onAddItem?.(subjectToAdd);
      
    } catch (error) {
          // 错误处理
      handleDropError(error as Error);
    }
  }, [onAddItem, handleDropError]);
  
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.SUBJECT,
    drop: (item: DragItem, monitor) => {      
      // 处理拖拽的科目并添加到画布
      if (item && item.id && item.name && onAddItem) {
        
        const subjectToAdd: ExtendedSubject = {
          id: item.id,
          name: item.name || item._dragName || '',
          parentId: item.parentId,
          parentName: item.parentName,
          grandParentName: item.grandParentName,
          assessmentDimension: item.assessmentDimension
            ? {
                id: item.assessmentDimension.id,
                name: item.assessmentDimension.name,
                createdAt: item.assessmentDimension.createdAt || '',
                updatedAt: item.assessmentDimension.updatedAt || '',
                enabled: typeof item.assessmentDimension.enabled === 'boolean' ? item.assessmentDimension.enabled : true,
              }
            : undefined,
          _dragName: item._dragName || item.name,
          _dragFullName: item._dragFullName || item.name,
          _originalDragName: item._originalDragName || item.name,
          _originalDragId: item._originalDragId || item.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        
        // 直接调用onAddItem添加科目到画布
        onAddItem(subjectToAdd);
      }
      
      return { moved: true };
    },
    hover: () => {
      setIsDraggingOver(true);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [onAddItem]);
  drop(dropRef);
  const isActive = isOver && canDrop;
  const scrollToTopLeft = useCallback((smooth = true) => {
    if (scrollWrapperRef.current) {
      scrollWrapperRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
  }, []);
  useEffect(() => {
  }, [templateType, canvasItems, columnSettings]);

  // 处理表格数据更新的useEffect，当canvasItems、rowBackgroundColors或columnSettings变化时触发
  useEffect(() => {
    if (templateType && templateType !== 'amoeba') {
      const newTableData = getTemplateData(templateType, canvasItems, rowBackgroundColors, columnSettings);
      setTableData(newTableData);
      if (onTableDataChangeRef.current) {
        onTableDataChangeRef.current(newTableData);
      }
    } else {
      setTableData(null);
    }
  }, [templateType, canvasItems, rowBackgroundColors, columnSettings]);

  // 只有当缩放级别变化为更大缩放时，才处理滚动位置
  useEffect(() => {
    if (zoomLevel.endsWith('%')) {
      const currentZoom = parseInt(zoomLevel, 10);
      const prevZoom = prevZoomLevelRef.current.endsWith('%') 
        ? parseInt(prevZoomLevelRef.current, 10) 
        : 100;
      
      // 只在缩放级别增大时滚动到顶部
      if (currentZoom > prevZoom && currentZoom > 100) {
        setTimeout(() => scrollToTopLeft(), 100);
      }
      
      // 更新上一次缩放级别引用
      prevZoomLevelRef.current = zoomLevel;
    }
  }, [zoomLevel, scrollToTopLeft]);

  const handleZoomChange = (zoomValue: string) => {
    // 保存当前缩放级别作为前一次缩放
    prevZoomLevelRef.current = zoomLevel;
    
    setCustomTransform(null);
    if (zoomValue === '适合屏幕' || zoomValue === '填满屏幕') {
      setSpecialZoomType(zoomValue);
      handleSpecialZoom(zoomValue);
    } else {
      setSpecialZoomType(null);
      setZoomLevel(zoomValue);
      if (zoomValue.endsWith('%')) {
        const newZoomValue = parseInt(zoomValue, 10) / 100;
        // 获取之前的缩放值
        const prevZoomValue = prevZoomLevelRef.current.endsWith('%')
          ? parseInt(prevZoomLevelRef.current, 10) / 100
          : 1;
        
        // 只有当新缩放值大于旧缩放值，且大于1时，才滚动到顶部
        if (newZoomValue > prevZoomValue && newZoomValue > 1) {
          setTimeout(() => scrollToTopLeft(), 50);
        }
      }
    }
  };
  const handleSpecialZoom = useCallback((type: string) => {
    if (!dropRef.current || !canvasContainerRef.current) return;
    const canvasRect = dropRef.current.getBoundingClientRect();
    const containerRect = canvasContainerRef.current.getBoundingClientRect();
    const contentWidth = canvasRect.width;
    const contentHeight = canvasRect.height;
    const availableWidth = containerRect.width - 40; 
    const availableHeight = containerRect.height - 40; 
    const widthRatio = availableWidth / contentWidth;
    const heightRatio = availableHeight / contentHeight;
    let scale: number;
    if (type === '适合屏幕') {
      scale = Math.min(widthRatio, heightRatio) * 0.95; 
    } else { 
      scale = Math.max(widthRatio, heightRatio);
    }
    setZoomLevel(type);
    setCustomTransform(`scale(${scale})`);
    
    // 提取上一次缩放级别的数值
    const prevZoom = prevZoomLevelRef.current.endsWith('%') 
      ? parseInt(prevZoomLevelRef.current, 10) / 100
      : 1;
    
    // 只有在新缩放比例大于上一次缩放比例且大于1时才滚动到顶部
    if (scale > prevZoom && scale > 1) {
      setTimeout(() => scrollToTopLeft(), 50);
    }
    
    // 更新上一次缩放级别参考值
    prevZoomLevelRef.current = `${Math.round(scale * 100)}%`;
  }, [scrollToTopLeft]);
  useEffect(() => {
    const handleResize = () => {
      if (specialZoomType) {
        handleSpecialZoom(specialZoomType);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [specialZoomType, handleSpecialZoom]);
  useEffect(() => {
    if (specialZoomType) {
      handleSpecialZoom(specialZoomType);
    }
  }, [specialZoomType, handleSpecialZoom]);
  const handleRemoveItem = useCallback((itemId: string) => {
    if (onRemoveItem) {
      onRemoveItem(itemId);
    }
  }, [onRemoveItem, canvasItems.length, templateType]);
  const renderTemplate = () => {
    if (!templateType || templateType === '') {
      return (
        <div className="empty-template">
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="拖拽科目到此处添加数据" 
          />
        </div>
      );
    }
    

    
    
    return getTemplateByType(
      templateType,
      canvasItems,
      selectedRowId,
      rowBackgroundColors,
      onRowSelect,
      columnSettings,
      onRemoveItem,
      readonly,
      onTableDataChange,
      frequencyType || '',
      reportType || '',
      allSubjects
    );
  };
  return (
    <div className="canvas-container" ref={canvasContainerRef}>
      {}
      <div className="canvas-scroll-wrapper" ref={scrollWrapperRef}>
        <div 
          ref={dropRef} 
          className={`template-canvas ${isActive ? 'active-drop' : ''}`}
          style={{ 
            transform: customTransform || (zoomLevel.endsWith('%') ? `scale(${parseInt(zoomLevel, 10) / 100})` : undefined),
            transformOrigin: 'top left', 
            transition: 'transform 0.3s ease'
          }}
        >
          {renderTemplate()}
        </div>
      </div>
      {}
      <div className="zoom-control">
        <Dropdown
          menu={{
            items: [
              { key: '400%', label: '400%' },
              { key: '200%', label: '200%' },
              { key: '100%', label: '100%' },
              { key: '50%', label: '50%' },
              { key: '适合屏幕', label: '适合屏幕' },
              { key: '填满屏幕', label: '填满屏幕' }
            ],
            onClick: ({ key }) => handleZoomChange(key)
          }}
        >
          <Button type="text">
            {zoomLevel} <DownOutlined />
          </Button>
        </Dropdown>
      </div>
    </div>
  );
};
export default TemplateCanvas;